/**
 * Cliente REST do gateway.
 *
 * O token de acesso vive **em memória**, nunca em storage: o painel abre dentro
 * de uma webview do SGA Desk/Gestor, e um token de acesso em storage
 * sobreviveria à sessão do usuário no sistema de origem.
 *
 * O refresh token é rotativo e vai para storage, com o escopo escolhido pelo
 * usuário no login:
 *
 * - **`sessionStorage`** (padrão do SSO) — morre ao fechar a aba. É o certo para
 *   a webview: quem sai do SGA Desk sai daqui também.
 * - **`localStorage`** ("Manter conectado") — sobrevive a fechar o navegador,
 *   até o TTL do refresh no gateway (7 dias por padrão). Serve para a equipe
 *   interna que abre o painel direto, sem passar pelo sistema de origem.
 *
 * Nos dois casos o token é revogado no servidor a cada uso — nunca há um
 * segredo de vida longa reutilizável em disco.
 */

import type { Marca } from './types';
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(/\/+$/, '');
export const WS_BASE = (import.meta.env.VITE_WS_BASE_URL ?? 'ws://localhost:8080').replace(
  /\/+$/,
  '',
);

const CHAVE_REFRESH = 'sga.messenger.refresh';
const CHAVE_PERSISTIR = 'sga.messenger.manter-conectado';
/** E-mail do último login, para pré-encher o formulário. Só o e-mail. */
const CHAVE_ULTIMO_EMAIL = 'sga.messenger.ultimo-email';

let tokenAcesso: string | null = null;
let tenantAtivo: string | null = null;
/** Chamado quando o refresh falha: o painel volta para a tela de login. */
let aoExpirar: (() => void) | null = null;

export function definirToken(token: string | null) {
  tokenAcesso = token;
}

export function definirTenant(slugOuId: string | null) {
  tenantAtivo = slugOuId;
}

export function tenantSelecionado() {
  return tenantAtivo;
}

/**
 * Storage que não explode quando o navegador o proíbe.
 *
 * O painel abre dentro de um iframe no PWA do SGA Desk, e nesse contexto
 * Safari e Chrome com storage particionado podem **negar acesso lançando
 * `SecurityError`** — não devolvendo `null`, mas atirando na primeira leitura.
 * Sem esta proteção, o painel morre na abertura com tela branca, e o erro só
 * aparece no console de quem souber abri-lo.
 *
 * Degradar é seguro aqui: o token de acesso vive em memória (ver o topo do
 * arquivo), então a sessão aberta continua funcionando. O que se perde é a
 * renovação silenciosa — quando o token expira, o SGA Desk reabre pelo SSO,
 * que é o fluxo normal dele de qualquer forma.
 */
const memoria = new Map<string, string>();

const storageSeguro = (real: () => Storage): Storage =>
  ({
    getItem: (k: string) => {
      try {
        return real().getItem(k);
      } catch {
        return memoria.get(k) ?? null;
      }
    },
    setItem: (k: string, v: string) => {
      try {
        real().setItem(k, v);
      } catch {
        memoria.set(k, v);
      }
    },
    removeItem: (k: string) => {
      try {
        real().removeItem(k);
      } catch {
        memoria.delete(k);
      }
    },
  }) as Storage;

const local = storageSeguro(() => localStorage);
const sessao = storageSeguro(() => sessionStorage);

/** Onde o refresh token mora, conforme a escolha do usuário. */
function storeRefresh(): Storage {
  return persistenciaAtiva() ? local : sessao;
}

export function persistenciaAtiva() {
  return local.getItem(CHAVE_PERSISTIR) === '1';
}

/**
 * Liga/desliga o "Manter conectado", movendo o token que já existe para o store
 * novo — sem isso, marcar a opção depois de logar perderia a sessão.
 */
export function definirPersistencia(persistir: boolean) {
  const atual = refreshGuardado();

  if (persistir) local.setItem(CHAVE_PERSISTIR, '1');
  else local.removeItem(CHAVE_PERSISTIR);

  sessao.removeItem(CHAVE_REFRESH);
  local.removeItem(CHAVE_REFRESH);
  if (atual) storeRefresh().setItem(CHAVE_REFRESH, atual);
}

export function definirRefresh(token: string | null) {
  // Remover dos dois stores: a flag pode ter mudado desde a última gravação.
  sessao.removeItem(CHAVE_REFRESH);
  local.removeItem(CHAVE_REFRESH);
  if (token) storeRefresh().setItem(CHAVE_REFRESH, token);
}

export function refreshGuardado() {
  return local.getItem(CHAVE_REFRESH) ?? sessao.getItem(CHAVE_REFRESH);
}

/** Pré-enche o campo de e-mail no próximo acesso. Senha nunca é guardada. */
export function lembrarEmail(email: string | null) {
  if (email) local.setItem(CHAVE_ULTIMO_EMAIL, email);
  else local.removeItem(CHAVE_ULTIMO_EMAIL);
}

export function emailLembrado() {
  return local.getItem(CHAVE_ULTIMO_EMAIL) ?? '';
}

export function aoPerderSessao(cb: () => void) {
  aoExpirar = cb;
}

export function tokenAtual() {
  return tokenAcesso;
}

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

interface Opcoes {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  corpo?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Interno: evita loop infinito de refresh. */
  jaTentouRefresh?: boolean;
}

function montarUrl(caminho: string, query?: Opcoes['query']) {
  const url = new URL(`${API_BASE}/api/v1${caminho}`);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, String(v));
  }
  return url.toString();
}

async function bruto<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (tokenAcesso) headers.authorization = `Bearer ${tokenAcesso}`;
  // Troca de contexto do admin global. Quem não é admin global tem o header
  // ignorado pelo gateway, então mandar sempre é seguro.
  if (tenantAtivo) headers['x-sga-tenant'] = tenantAtivo;
  if (opcoes.corpo !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(montarUrl(caminho, opcoes.query), {
    method: opcoes.metodo ?? 'GET',
    headers,
    body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
  });

  // 401 com refresh disponível: renova uma vez e repete a chamada.
  if (res.status === 401 && !opcoes.jaTentouRefresh && refreshGuardado()) {
    const renovou = await renovarSessao();
    if (renovou) return bruto<T>(caminho, { ...opcoes, jaTentouRefresh: true });
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    const erro = payload?.erro;
    if (res.status === 401) {
      definirToken(null);
      definirRefresh(null);
      aoExpirar?.();
    }
    throw new ErroApi(
      res.status,
      erro?.codigo ?? 'erro_desconhecido',
      erro?.mensagem ?? `O gateway respondeu ${res.status}.`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Renovação em voo, compartilhada.
 *
 * O refresh token é rotativo: quem usa, invalida. Sem esta dedupe, duas
 * chamadas concorrentes (várias requisições tomando 401 juntas, ou o efeito de
 * bootstrap rodando duas vezes sob StrictMode) corriam assim: a primeira
 * rotaciona e grava o token novo, a segunda apresenta o token já revogado,
 * recebe 401 e apaga o que a primeira acabou de gravar — derrubando a sessão.
 */
let renovacaoEmVoo: Promise<boolean> | null = null;

/** Renova o par token/refresh. `false` quando o refresh também morreu. */
function renovarSessao(): Promise<boolean> {
  if (renovacaoEmVoo) return renovacaoEmVoo;

  renovacaoEmVoo = renovarAgora().finally(() => {
    renovacaoEmVoo = null;
  });
  return renovacaoEmVoo;
}

async function renovarAgora(): Promise<boolean> {
  const refresh = refreshGuardado();
  if (!refresh) return false;

  try {
    const res = await fetch(montarUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh, tenant: tenantAtivo }),
    });
    if (!res.ok) {
      definirRefresh(null);
      return false;
    }
    const sessao = await res.json();
    definirToken(sessao.token);
    definirRefresh(sessao.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(caminho: string, query?: Opcoes['query']) => bruto<T>(caminho, { query }),
  post: <T>(caminho: string, corpo?: unknown) => bruto<T>(caminho, { metodo: 'POST', corpo }),
  patch: <T>(caminho: string, corpo?: unknown) => bruto<T>(caminho, { metodo: 'PATCH', corpo }),
  put: <T>(caminho: string, corpo?: unknown) => bruto<T>(caminho, { metodo: 'PUT', corpo }),
  del: <T>(caminho: string) => bruto<T>(caminho, { metodo: 'DELETE' }),
  renovarSessao,
};

/** URL de download com o token na query (o browser não manda header em `<a>`). */
export function urlExportacao(caminho: string, query?: Opcoes['query']) {
  const url = new URL(montarUrl(caminho, query));
  if (tokenAcesso) url.searchParams.set('token', tokenAcesso);
  return url.toString();
}

/** Upload de mídia (áudio, imagem, boleto, nota fiscal). */
export async function subirMidia(arquivo: File) {
  const form = new FormData();
  form.append('arquivo', arquivo, arquivo.name);

  const headers: Record<string, string> = {};
  if (tokenAcesso) headers.authorization = `Bearer ${tokenAcesso}`;
  if (tenantAtivo) headers['x-sga-tenant'] = tenantAtivo;

  const res = await fetch(montarUrl('/midia'), { method: 'POST', headers, body: form });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ErroApi(
      res.status,
      payload?.erro?.codigo ?? 'upload_falhou',
      payload?.erro?.mensagem ?? 'Não consegui subir o arquivo.',
    );
  }
  return (await res.json()) as {
    id: string;
    url: string;
    arquivo: string;
    mime: string;
    tipo_sugerido: string;
    bytes: number;
  };
}

/** Sobe o logo da marca do tenant. Devolve a marca atualizada. */
export async function subirLogo(arquivo: File) {
  const form = new FormData();
  form.append('arquivo', arquivo, arquivo.name);

  const headers: Record<string, string> = {};
  if (tokenAcesso) headers.authorization = `Bearer ${tokenAcesso}`;
  if (tenantAtivo) headers['x-sga-tenant'] = tenantAtivo;

  const res = await fetch(montarUrl('/marca/logo'), { method: 'POST', headers, body: form });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ErroApi(
      res.status,
      payload?.erro?.codigo ?? 'upload_falhou',
      payload?.erro?.mensagem ?? 'Não consegui subir o logo.',
    );
  }
  return (await res.json()) as { marca: Marca };
}
